<script setup lang="ts">
import { IconMail, IconLogout2, IconKey } from '@tabler/icons-vue';
import { useAuthStore } from '@/stores/auth';
import { gitInfo } from "~/data/gitInfo";
const authStore = useAuthStore();

// --- Cambiar mi contraseña (autoservicio, disponible en todos los perfiles) ---
const changePasswordDialog = ref(false);
const currentPassword = ref('');
const newPassword = ref('');
const confirmNewPassword = ref('');
const changePasswordError = ref('');
const changingPassword = ref(false);
const changePasswordSuccess = ref(false);

function openChangePassword() {
    currentPassword.value = '';
    newPassword.value = '';
    confirmNewPassword.value = '';
    changePasswordError.value = '';
    changePasswordSuccess.value = false;
    changePasswordDialog.value = true;
}

const changePasswordValid = computed(
    () => currentPassword.value.length > 0
        && newPassword.value.length >= 8
        && newPassword.value === confirmNewPassword.value,
);

async function changePassword() {
    if (!changePasswordValid.value) return;
    changingPassword.value = true;
    changePasswordError.value = '';
    try {
        const baseUrl = useRuntimeConfig().public.apiBase;
        await fetchWrapper.post(`${baseUrl}/base/users/change_password/`, {
            current_password: currentPassword.value,
            new_password: newPassword.value,
        });
        changePasswordSuccess.value = true;
        currentPassword.value = '';
        newPassword.value = '';
        confirmNewPassword.value = '';
    } catch (e: any) {
        changePasswordError.value = e?.detail || e?.message || 'No se pudo cambiar la contraseña.';
    } finally {
        changingPassword.value = false;
    }
}

// Nombre para mostrar en el saludo, en orden de preferencia:
// 1) thirdparty.first_name/middle_name (persona real asociada, ej. un
//    cliente/vendedor con ficha de tercero) — con middle_name opcional, ya
//    que antes un `.toLowerCase()` sobre middle_name nulo rompía el saludo
//    entero.
// 2) user.first_name/last_name (el propio User de Django) — usado cuando no
//    hay thirdparty asociado, como en las cuentas de vendedor/picker/admin
//    creadas directamente sin ficha de tercero.
// 3) la parte antes del @ del correo, para que el saludo nunca quede solo
//    en "¡Hola".
const greetingName = computed(() => {
    const user = authStore?.userSession?.user;
    const thirdparty = user?.thirdparty;
    if (thirdparty?.first_name) {
        const parts = [thirdparty.first_name, thirdparty.middle_name].filter(Boolean).map((p: string) => p.toLowerCase());
        return parts.join(' ');
    }
    if (user?.first_name) {
        return [user.first_name, user.last_name].filter(Boolean).join(' ').toLowerCase();
    }
    return user?.email ? user.email.split('@')[0] : '';
});
</script>

<template>
  <!-- ---------------------------------------------- -->
  <!-- notifications DD -->
  <!-- ---------------------------------------------- -->
  <v-menu :close-on-content-click="true">
    <template v-slot:activator="{ props }">
      <v-btn variant="text" v-bind="props" icon >
        <v-avatar size="40" class="border border-2 border-grey200" >
          <img src="/images/profile/user-1.jpg?p=2" width="40" />
        </v-avatar>
        <slot>
        </slot>
      </v-btn>
    </template>
    <v-sheet rounded="md" width="340" elevation="6" class="mt-2">
      <div class="px-4 pt-2">
        <div class="d-flex align-center mt-4 pb-4">
          <v-avatar size="60">
            <img v-if="authStore?.userSession?.user?.user_info?.picture" 
              :src="authStore?.userSession?.user?.user_info?.picture" 
              onerror="this.src='/images/profile/user-1.jpg';" :width="60" />
            <img v-else src="/images/profile/user-1.jpg" :width="60" />
          </v-avatar>
          <div class="ml-2">
            {{ $t('index.hello') }} <span v-if="greetingName" class="text-h6 text-capitalize">{{ greetingName }}!</span>
            <!-- <span class="text-subtitle-1 font-weight-regular textSecondary"
              >Designer</span
            > -->
            <div class="d-flex align-center mt-1">
              <IconMail size="18" stroke-width="1.5" />
              <span class="text-subtitle-1 font-weight-regular textSecondary ml-2" style="text-overflow: ellipsis; max-width: 200px; overflow: hidden;">{{
                authStore?.userSession?.user?.email }}
              </span>
            </div>
          </div>
        </div>
        <v-divider></v-divider>
      </div>
      <div class="pt-4 pb-2 px-8 text-center">
        <v-btn color="primary" variant="tonal" size="large" rounded="xl" block class="mb-3" @click="openChangePassword">
          <IconKey size="20" class="mr-2" /> Cambiar contraseña
        </v-btn>
        <v-btn color="secondary" size="large" rounded="xl" block @click="authStore.initiateLogout('profile-btn-logout-click()')">
          <IconLogout2 size="22" class="mr-2" />  {{ $t('Logout') }}
        </v-btn>
      </div>
      <p class="text-center text-grey600 text-10 py-2"><span>{{ useRuntimeConfig().public.nuxt_env.substring(0, 3) !== 'dev' ? `${gitInfo.split(' ')[0]} (${gitInfo.split(' ')[4]}` : gitInfo }}</span></p>
    </v-sheet>
  </v-menu>

  <v-dialog v-model="changePasswordDialog" max-width="420">
    <v-card title="Cambiar contraseña">
      <v-card-text>
        <template v-if="changePasswordSuccess">
          <v-alert type="success" variant="tonal">Contraseña actualizada correctamente.</v-alert>
        </template>
        <template v-else>
          <v-text-field
            v-model="currentPassword"
            label="Contraseña actual"
            type="password"
            variant="outlined"
            density="comfortable"
            class="mb-3"
            hide-details
          />
          <v-text-field
            v-model="newPassword"
            label="Nueva contraseña"
            type="password"
            variant="outlined"
            density="comfortable"
            hint="Mínimo 8 caracteres."
            persistent-hint
            class="mb-3"
          />
          <v-text-field
            v-model="confirmNewPassword"
            label="Confirmar nueva contraseña"
            type="password"
            variant="outlined"
            density="comfortable"
            :error="confirmNewPassword.length > 0 && confirmNewPassword !== newPassword"
            :error-messages="confirmNewPassword.length > 0 && confirmNewPassword !== newPassword ? 'Las contraseñas no coinciden.' : ''"
          />
          <v-alert v-if="changePasswordError" type="error" variant="tonal" density="compact" class="mt-2">
            {{ changePasswordError }}
          </v-alert>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="changePasswordDialog = false">{{ changePasswordSuccess ? 'Cerrar' : 'Cancelar' }}</v-btn>
        <v-btn
          v-if="!changePasswordSuccess"
          color="primary"
          variant="flat"
          :disabled="!changePasswordValid"
          :loading="changingPassword"
          @click="changePassword"
        >
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
